import {
  createRouter,
  createWebHistory,
  type NavigationGuardNext,
  type RouteLocationNormalized,
} from 'vue-router';
import HomeView from '../views/HomeView.vue';
import { useAuthStore } from '@/stores/authStore';
import { useMigrationStatusStore } from '@/stores/migrationStatusStore';
import { EMPLOYEE_ENTITY, TENANT_ENTITY } from '@/models/migration';
import { useFeatureFlags } from '@erebus/feature-flags';
import { getRuntimeConfig } from '@erebus/runtime-config';
import MainLayout from '@/layouts/MainLayout.vue';
import { legacyTabRedirect, REPORT_ROUTES } from '@/composables/useReportRoutes';

/** Every reports page is for super admins, behind the ivr_ops_dashboard flag. */
const REPORTS_META = {
  requiresAuth: true,
  requiresSuperAdmin: true,
  requiresFeature: 'ivr_ops_dashboard',
  title: 'reports.title',
};

/** Every tenant template page is for super admins, behind the tenant_templates flag. */
const TEMPLATES_META = {
  requiresAuth: true,
  requiresSuperAdmin: true,
  requiresFeature: 'tenant_templates',
  title: 'tenantTemplates.title',
};

/**
 * One reports page. It has no component of its own (components: {}):
 * ReportsView, on the parent route, draws the page for this route name.
 */
function reportPage(path: string, name: string) {
  return { path, name, meta: REPORTS_META, components: {} };
}

/**
 * Scrolls to the element a link's hash names, such as the IVR operations
 * section on the reports home page (/reports#ivr). Any other navigation
 * leaves the scroll position alone, as before.
 *
 * The tenant page uses its hash to pick a tab (#locations), so the router does not
 * scroll there.
 */
export function scrollToHash(to: RouteLocationNormalized) {
  if (to.name === 'tenant') return false;
  return to.hash ? { el: to.hash } : false;
}

const router = createRouter({
  history: createWebHistory(import.meta.env.BASE_URL),
  scrollBehavior: scrollToHash,
  routes: [
    {
      path: '/',
      component: MainLayout,
      children: [
        {
          path: '',
          name: 'home',
          component: HomeView,
          meta: { requiresAuth: true },
        },
        {
          path: '/tenants',
          name: 'tenants',
          component: () => import('../views/TenantsView.vue'),
          meta: { requiresAuth: true, title: 'tenants.title' },
        },
        {
          path: '/tenants/new',
          name: 'tenantNew',
          component: () => import('../views/TenantCreateView.vue'),
          meta: {
            requiresAuth: true,
            requiresSuperAdmin: true,
            requiresTenantWrites: true,
            title: 'tenants.new',
          },
        },
        {
          path: '/tenants/:tenantID',
          name: 'tenant',
          component: () => import('../views/TenantView.vue'),
          props: true,
          meta: { requiresAuth: true, title: 'tenant.title' },
        },
        {
          path: '/tenants/:tenantID/locations',
          meta: { requiresAuth: true },
          children: [
            {
              // The location list now lives on the tenant page. Old links land on its card.
              path: '',
              name: 'tenantLocations',
              redirect: (to) => ({
                name: 'tenant',
                params: { tenantID: to.params.tenantID },
                hash: '#locations',
              }),
            },
            {
              path: 'new',
              name: 'tenantLocationNew',
              component: () => import('../views/TenantLocationDetailView.vue'),
              props: true,
              meta: {
                requiresAuth: true,
                title: 'locations.new',
              },
            },
            {
              path: ':locationID',
              name: 'tenantLocationDetail',
              component: () => import('../views/TenantLocationDetailView.vue'),
              props: true,
              meta: {
                requiresAuth: true,
                title: 'locations.title',
              },
            },
          ],
        },
        {
          // Case tags now live in a tab on the tenant page (PRX-2554). Old links land on it.
          path: '/tenants/:tenantID/case-tags',
          name: 'tenantCaseTags',
          redirect: (to) => ({
            name: 'tenant',
            params: { tenantID: to.params.tenantID },
            hash: '#case-tags',
          }),
        },
        {
          // Tenant templates (PRX-2424): platform admins only, behind tenant_templates.
          path: '/tenant-templates',
          meta: TEMPLATES_META,
          children: [
            {
              path: '',
              name: 'tenantTemplates',
              component: () => import('../views/TenantTemplatesView.vue'),
              meta: TEMPLATES_META,
            },
            {
              path: 'new',
              name: 'tenantTemplateNew',
              component: () => import('../views/TenantTemplateNewView.vue'),
              meta: { ...TEMPLATES_META, title: 'tenantTemplates.new' },
            },
            {
              path: ':templateID',
              name: 'tenantTemplate',
              component: () => import('../views/TenantTemplateView.vue'),
              props: true,
              meta: { ...TEMPLATES_META, title: 'tenantTemplate.title' },
            },
          ],
        },
        {
          // Old /clients links (bookmarks, emails) keep working. vue-router keeps the query and hash.
          path: '/clients/:rest(.*)*',
          redirect: (to) => ({
            path: ['/tenants', ...(to.params.rest as string[])].join('/'),
          }),
        },
        {
          path: '/ivrFlows',
          meta: { requiresAuth: true, title: 'ivrFlows.title' },
          children: [
            {
              path: '',
              name: 'ivrFlows',
              component: () => import('../views/IVRFlowsView.vue'),
              meta: { requiresAuth: true, title: 'ivrFlows.title' },
            },
            {
              path: 'new',
              name: 'newIVRFlow',
              component: () => import('../views/NewIVRFlowView.vue'),
              meta: { requiresAuth: true, title: 'ivrFlow.new.title' },
            },
            {
              path: 'import',
              name: 'importIVRFlow',
              component: () => import('../views/ImportIVRFlowView.vue'),
              meta: { requiresAuth: true, title: 'ivrFlows.importTitle' },
            },
            {
              path: ':id',
              name: 'editIVRFlow',
              component: () => import('../views/IVRFlowView.vue'),
              props: true,
              meta: { requiresAuth: true, title: 'ivrFlow.title' },
            },
          ],
        },
        {
          path: '/ivrNumbers',
          meta: { requiresAuth: true, title: 'ivrNumbers.title' },
          children: [
            {
              path: '',
              name: 'ivrNumbers',
              component: () => import('../views/IvrNumbersView.vue'),
              meta: { requiresAuth: true, title: 'ivrNumbers.title' },
            },
            {
              path: 'new',
              name: 'ivrNumberNew',
              component: () => import('../views/IvrNumberDetailView.vue'),
              meta: { requiresAuth: true, title: 'ivrNumbers.createTitle' },
            },
            {
              path: ':id',
              name: 'ivrNumberDetail',
              component: () => import('../views/IvrNumberDetailView.vue'),
              props: true,
              meta: { requiresAuth: true, title: 'ivrNumbers.editTitle' },
            },
          ],
        },
        {
          path: '/ivrMessaging',
          meta: { requiresAuth: true, title: 'ivrMessaging.title' },
          children: [
            {
              path: '',
              name: 'ivrMessaging',
              component: () => import('../views/IvrMessagingView.vue'),
              meta: { requiresAuth: true, title: 'ivrMessaging.title' },
            },
          ],
        },
        {
          path: '/featureFlags',
          name: 'featureFlags',
          component: () => import('../views/FeatureFlagsView.vue'),
          meta: { requiresAuth: true, title: 'featureFlags.title' },
        },
        {
          path: '/modules',
          name: 'moduleManagement',
          component: () => import('../views/ModuleTiersView.vue'),
          meta: { requiresAuth: true, requiresSuperAdmin: true, title: 'modules.tiers.title' },
        },
        {
          path: '/migration',
          name: 'migrationState',
          component: () => import('../views/MigrationStateView.vue'),
          meta: {
            requiresAuth: true,
            requiresSuperAdmin: true,
            requiresFeature: 'migration_console_v1',
            title: 'migration.title',
          },
        },
        {
          // Find a user and reset their MFA (PRX-2160). Platform admins only,
          // behind auth_admin_mfa_reset, and only in cookie auth mode.
          path: '/users',
          name: 'users',
          component: () => import('../views/UsersView.vue'),
          meta: {
            requiresAuth: true,
            requiresSuperAdmin: true,
            requiresFeature: 'auth_admin_mfa_reset',
            title: 'users.title',
          },
        },
        {
          // The people directory (PRX-2519). Platform admins only. No flag: it
          // opens when the employee migration state is soaking or later.
          path: '/people',
          name: 'people',
          component: () => import('../views/PeopleView.vue'),
          meta: {
            requiresAuth: true,
            requiresSuperAdmin: true,
            requiresEmployeeReads: true,
            title: 'people.title',
          },
        },
        {
          // One person (PRX-2520). Same gate as the list.
          path: '/people/:personId',
          name: 'person',
          component: () => import('../views/PersonDetailView.vue'),
          props: true,
          meta: {
            requiresAuth: true,
            requiresSuperAdmin: true,
            requiresEmployeeReads: true,
            title: 'people.title',
          },
        },
        {
          // ReportsView stays mounted across every reports page, so the shared
          // period and timezone carry over. It picks the page by route name.
          path: '/reports',
          component: () => import('../views/ReportsView.vue'),
          meta: REPORTS_META,
          children: [
            {
              ...reportPage('', REPORT_ROUTES.home),
              // Old links were /reports?tab=<tab>&...; send them to their page.
              beforeEnter: (to) => legacyTabRedirect(to),
            },
            reportPage('ivr/doctors', REPORT_ROUTES.doctors),
            reportPage('ivr/doctors/:doctorId', REPORT_ROUTES.doctor),
            reportPage('ivr/premiums', REPORT_ROUTES.premiums),
            reportPage('ivr/schedule-groups', REPORT_ROUTES.scheduleGroups),
            reportPage('ivr/schedule-groups/:groupKey', REPORT_ROUTES.scheduleGroup),
            reportPage('ivr/schedule-line', REPORT_ROUTES.scheduleLine),
            reportPage('ivr/phone-numbers', REPORT_ROUTES.phoneNumbers),
          ],
        },
        {
          path: '/scheduling',
          meta: { requiresAuth: true, requiresFeature: 'scheduling_v1', title: 'scheduling.title' },
          children: [
            {
              path: 'schedule',
              name: 'schedule',
              component: () => import('../views/ScheduleView.vue'),
              meta: {
                requiresAuth: true,
                requiresFeature: 'scheduling_v1',
                title: 'scheduling.schedule.title',
              },
            },
            {
              path: 'groups',
              name: 'scheduleGroups',
              component: () => import('../views/ScheduleGroupsView.vue'),
              meta: {
                requiresAuth: true,
                requiresFeature: 'scheduling_v1',
                title: 'scheduling.groups.title',
              },
            },
            {
              path: 'groups/new',
              name: 'scheduleGroupNew',
              component: () => import('../views/ScheduleGroupDetailView.vue'),
              meta: {
                requiresAuth: true,
                requiresFeature: 'scheduling_v1',
                requiresScheduleAdmin: true,
                title: 'scheduling.groups.createTitle',
              },
            },
            {
              path: 'groups/:id',
              name: 'scheduleGroupDetail',
              component: () => import('../views/ScheduleGroupDetailView.vue'),
              props: true,
              meta: {
                requiresAuth: true,
                requiresFeature: 'scheduling_v1',
                requiresScheduleAdmin: true,
                title: 'scheduling.groups.editTitle',
              },
            },
            {
              path: 'lines',
              name: 'scheduleLines',
              component: () => import('../views/ScheduleLinesView.vue'),
              meta: {
                requiresAuth: true,
                requiresFeature: 'scheduling_v1',
                title: 'scheduling.lines.title',
              },
            },
            {
              path: 'lines/new',
              name: 'scheduleLineNew',
              component: () => import('../views/ScheduleLineDetailView.vue'),
              meta: {
                requiresAuth: true,
                requiresFeature: 'scheduling_v1',
                requiresScheduleAdmin: true,
                title: 'scheduling.lines.createTitle',
              },
            },
            {
              path: 'lines/:id',
              name: 'scheduleLineDetail',
              component: () => import('../views/ScheduleLineDetailView.vue'),
              props: true,
              meta: {
                requiresAuth: true,
                requiresFeature: 'scheduling_v1',
                requiresScheduleAdmin: true,
                title: 'scheduling.lines.editTitle',
              },
            },
            {
              path: 'lines/:id/schedule',
              name: 'scheduleLineSchedule',
              component: () => import('../views/ScheduleLineScheduleView.vue'),
              props: true,
              meta: {
                requiresAuth: true,
                requiresFeature: 'scheduling_v1',
                requiresScheduleAdmin: true,
                title: 'scheduling.monthSchedule.title',
              },
            },
            {
              path: 'lines/:id/eligibility',
              name: 'scheduleLineEligibility',
              component: () => import('../views/ScheduleLineEligibilityView.vue'),
              props: true,
              meta: {
                requiresAuth: true,
                requiresFeature: 'scheduling_v1',
                requiresScheduleAdmin: true,
                title: 'scheduling.eligibility.title',
              },
            },
          ],
        },
        {
          path: '/security',
          name: 'security',
          component: () => import('../views/SecurityView.vue'),
          meta: { requiresAuth: true, requiresFeature: 'auth_cutover' },
        },
      ],
    },
    {
      path: '/login',
      name: 'login',
      meta: { requiresAuth: false },
      component: () => import('../views/LoginView.vue'),
    },
    {
      path: '/logout',
      name: 'logout',
      meta: { requiresAuth: false },
      component: () => import('../views/LogoutView.vue'),
    },
    {
      path: '/secondFactor',
      name: 'secondFactor',
      meta: { requiresAuth: false },
      component: () => import('../views/SecondFactorView.vue'),
    },
    {
      // A forced reset happens before the user has a session, so this sits
      // outside MainLayout with login and secondFactor. `requiresResetToken`
      // is what stands in for `requiresAuth` here: the token is the only
      // credential the user holds at this point.
      path: '/reset-password',
      name: 'resetPassword',
      meta: { requiresAuth: false, requiresResetToken: true },
      component: () => import('../views/ResetPasswordView.vue'),
    },
    {
      // MFA setup during login (PRX-2355). Like the forced reset, the user has
      // no session yet: the setup token from login stands in for one.
      path: '/mfa-setup',
      name: 'mfaSetup',
      meta: { requiresAuth: false, requiresMFASetup: true },
      component: () => import('../views/MfaSetupView.vue'),
    },
  ],
});

router.beforeEach(
  async (to: RouteLocationNormalized, from: RouteLocationNormalized, next: NavigationGuardNext) => {
    const authStore = useAuthStore();
    const featureFlags = useFeatureFlags();

    // Checked before requiresAuth, because a user being forced to reset their
    // password has no session yet — the auth_cutover branch further down sits
    // inside the requiresAuth block and would never run for this route.
    if (to.matched.some((record) => record.meta.requiresResetToken)) {
      if (!featureFlags.isCached.value) {
        await featureFlags.refresh();
      }
      // Same two gates as /security, plus the token. Only the Go backend
      // returns must_reset, so on a legacy host this screen has nothing to
      // talk to.
      const { useCookieAuth } = getRuntimeConfig();
      if (
        !useCookieAuth ||
        !featureFlags.isEnabled('auth_cutover').value ||
        !authStore.pendingResetToken
      ) {
        next({ name: 'login' });
      } else {
        next();
      }
      return;
    }

    // Only login hands out a setup token, and only the Go backend's login, so
    // holding one is the whole check.
    if (to.matched.some((record) => record.meta.requiresMFASetup)) {
      if (!authStore.pendingMFASetup) {
        next({ name: 'login' });
      } else {
        next();
      }
      return;
    }

    // Check if the route requires authentication
    if (to.matched.some((record) => record.meta.requiresAuth)) {
      // Check if the user is not authenticated
      if (!authStore.isAuthenticated) {
        // Redirect to the login page
        next({ name: 'login' });
      } else if (to.matched.some((record) => record.meta.requiresSuperAdmin)) {
        // Check if the route requires super admin access
        if (!authStore.isSuperAdmin) {
          // Redirect to home if user is not a super admin
          next({ name: 'home' });
        } else if (
          to.matched.some((record) => record.meta.requiresFeature === 'migration_console_v1')
        ) {
          // Super-admin routes may additionally be gated behind a feature flag so the
          // UI can ship dark and be enabled per-environment.
          if (!featureFlags.isCached.value) {
            await featureFlags.refresh();
          }
          if (!featureFlags.hasMigrationConsoleV1.value) {
            next({ name: 'home' });
          } else {
            next();
          }
        } else if (
          to.matched.some((record) => record.meta.requiresFeature === 'ivr_ops_dashboard')
        ) {
          // The IVR ops reports ship dark behind their own flag.
          if (!featureFlags.isCached.value) {
            await featureFlags.refresh();
          }
          if (!featureFlags.hasIvrOpsDashboard.value) {
            next({ name: 'home' });
          } else {
            next();
          }
        } else if (
          to.matched.some((record) => record.meta.requiresFeature === 'auth_admin_mfa_reset')
        ) {
          // The backend's permission check needs a cookie session, so the
          // screen is closed in bearer mode too.
          if (!featureFlags.isCached.value) {
            await featureFlags.refresh();
          }
          const { useCookieAuth } = getRuntimeConfig();
          if (!useCookieAuth || !featureFlags.hasAuthAdminMfaReset.value) {
            next({ name: 'home' });
          } else {
            next();
          }
        } else if (
          to.matched.some((record) => record.meta.requiresFeature === 'tenant_templates')
        ) {
          // The tenant template pages ship dark behind their own flag.
          if (!featureFlags.isCached.value) {
            await featureFlags.refresh();
          }
          if (!featureFlags.isEnabled('tenant_templates').value) {
            next({ name: 'home' });
          } else {
            next();
          }
        } else if (to.matched.some((record) => record.meta.requiresTenantWrites)) {
          // Creating a tenant writes to Erebus, which needs the tenant
          // migration state at cutover or complete. If the state can't be
          // loaded, go to the tenant list, which shows the error.
          const migrationStatus = useMigrationStatusStore();
          try {
            await migrationStatus.load();
          } catch (err) {
            console.error('Failed to load tenant migration state:', err);
          }
          if (!migrationStatus.writesToErebus(TENANT_ENTITY)) {
            next({ name: 'tenants' });
          } else {
            next();
          }
        } else if (to.matched.some((record) => record.meta.requiresEmployeeReads)) {
          // The people list reads from Erebus, which needs the employee
          // migration state at soaking or later. If the state can't be
          // loaded, go home.
          const migrationStatus = useMigrationStatusStore();
          try {
            await migrationStatus.load();
          } catch (err) {
            console.error('Failed to load employee migration state:', err);
          }
          if (!migrationStatus.readsFromErebus(EMPLOYEE_ENTITY)) {
            next({ name: 'home' });
          } else {
            next();
          }
        } else {
          // Allow the navigation
          next();
        }
      } else if (to.matched.some((record) => record.meta.requiresFeature === 'scheduling_v1')) {
        // Ensure feature flags are loaded before checking
        if (!featureFlags.isCached.value) {
          await featureFlags.refresh();
        }
        // Check if the route requires the scheduling_v1 feature flag
        if (!featureFlags.hasSchedulingV1.value) {
          // Redirect to home if feature is not enabled
          next({ name: 'home' });
        } else if (to.matched.some((record) => record.meta.requiresScheduleAdmin)) {
          // Schedule mutation routes require admin or masterAdmin role.
          // isScheduleAdmin is source-aware (cookie vs legacy auth).
          if (!authStore.isScheduleAdmin) {
            next({ name: 'schedule' });
          } else {
            next();
          }
        } else {
          // Allow the navigation
          next();
        }
      } else if (to.matched.some((record) => record.meta.requiresFeature === 'auth_cutover')) {
        // Ensure feature flags are loaded before checking
        if (!featureFlags.isCached.value) {
          await featureFlags.refresh();
        }
        // Both useCookieAuth runtime config AND auth_cutover feature flag must be enabled
        const { useCookieAuth } = getRuntimeConfig();
        if (!useCookieAuth || !featureFlags.isEnabled('auth_cutover').value) {
          // Redirect to home if either condition is not met
          next({ name: 'home' });
        } else {
          // Allow the navigation
          next();
        }
      } else {
        // Allow the navigation
        next();
      }
    } else if (
      to.matched.some((record) => record.meta.requiresNoAuth) &&
      authStore.isAuthenticated
    ) {
      // If the route does not require authentication and the user is authenticated, redirect to the logout page
      next({ name: 'logout' });
    } else {
      // Allow the navigation
      next();
    }
  },
);

export default router;
